import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Input } from '../components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Mail, Search, TrendingUp, CheckCircle2, RefreshCw, ArrowLeft,
  AlertTriangle, XCircle, ShieldCheck, Activity, Inbox, Filter,
} from 'lucide-react';
import BackButton from '../components/BackButton';
import { getApiBase } from '../api/httpClient';
import './AdminModeration.css';
import './AdminSystemHealth.css';

// iter272: same-origin smart base — the production wassla.tech build
// bakes a cross-origin REACT_APP_BACKEND_URL which CORS blocks, causing
// the red toast spam the user reported on the system-health page.
// getApiBase() returns '' (relative URL) on custom production domains,
// preserves env URL on preview/dev (iter263 pattern).
const API = getApiBase();

/**
 * Admin · System Health
 * ---------------------------------------------------------------
 * Mobile-first, single scrollable page exposing the 4 admin endpoints
 * shipped in Phase A. Reuses the AdminModeration styling (admin-page /
 * admin-header / admin-back-btn) so we don't introduce new visual
 * primitives. All actions surface sonner toasts and require an
 * AlertDialog confirmation. EN + AR + RTL aware.
 *
 * Endpoints consumed (NONE created here):
 *   GET  /api/admin/email/status
 *   POST /api/admin/email/domains/{id}/verify
 *   GET  /api/admin/search/zero-result-queries
 *   GET  /api/admin/search/alias-suggestions
 *   POST /api/admin/search/alias-suggestions/{nq}/decision
 *   GET  /api/admin/search/alias-overrides
 */
export default function AdminSystemHealth() {
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const isArabic = lang === 'ar';
  const dir = isArabic ? 'rtl' : 'ltr';

  // Section data
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailLoading, setEmailLoading] = useState(true);

  const [zeroResults, setZeroResults] = useState(null);
  const [zeroResultsLoading, setZeroResultsLoading] = useState(true);
  const [zeroResultsDays, setZeroResultsDays] = useState(7);
  const [zeroResultsFilter, setZeroResultsFilter] = useState('');
  const [zeroResultsError, setZeroResultsError] = useState(null);

  const [suggestions, setSuggestions] = useState(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(true);
  const [suggestionsFilter, setSuggestionsFilter] = useState('');
  const [suggestionsError, setSuggestionsError] = useState(null);

  const [overrides, setOverrides] = useState(null);
  const [overridesLoading, setOverridesLoading] = useState(true);
  const [overridesFilter, setOverridesFilter] = useState('');
  const [overridesError, setOverridesError] = useState(null);
  const [overridesMeta, setOverridesMeta] = useState({ cache_size: 0, auto_promote_enabled: false });

  // Confirm modal state
  const [confirm, setConfirm] = useState({
    open: false,
    title: '',
    description: '',
    onConfirm: null,
    danger: false,
  });

  const authHeader = useMemo(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  // ── data loaders ────────────────────────────────────────────────────
  const [emailError, setEmailError] = useState(null);
  const loadEmail = useCallback(async () => {
    setEmailLoading(true);
    setEmailError(null);
    try {
      const r = await fetch(`${API}/api/admin/email/status`, { headers: authHeader });
      if (!r.ok) {
        const body = await r.text().catch(() => '');
        throw new Error(`HTTP ${r.status}${body ? `: ${body.slice(0, 120)}` : ''}`);
      }
      setEmailStatus(await r.json());
    } catch (e) {
      // iter273 — inline banner, not a red toast (Resend not configured
      // in preview returns 500; the operator already knows about it).
      setEmailError(e?.message || 'Failed to load');
      setEmailStatus(null);
    } finally {
      setEmailLoading(false);
    }
  }, [authHeader]);

  const loadZeroResults = useCallback(async () => {
    setZeroResultsLoading(true);
    setZeroResultsError(null);
    try {
      const r = await fetch(
        `${API}/api/admin/search/zero-result-queries?days=${zeroResultsDays}&limit=100`,
        { headers: authHeader, credentials: 'include' },
      );
      if (!r.ok) {
        const body = await r.text().catch(() => '');
        throw new Error(`HTTP ${r.status}${body ? `: ${body.slice(0, 120)}` : ''}`);
      }
      const data = await r.json();
      // iter272 — backend may return [] OR {queries: [...]} OR null.
      // Normalise to an array so the render code can do .map without
      // crashing when the collection is empty.
      setZeroResults(Array.isArray(data) ? data : (data?.queries || []));
    } catch (e) {
      // iter272 — surface the failure as an inline banner inside the
      // section, NOT as a red toast. Empty databases and network blips
      // shouldn't spam the operator.
      setZeroResultsError(e?.message || 'Failed to load');
      setZeroResults([]);
    } finally {
      setZeroResultsLoading(false);
    }
  }, [authHeader, zeroResultsDays]);

  const loadSuggestions = useCallback(async () => {
    setSuggestionsLoading(true);
    setSuggestionsError(null);
    try {
      const r = await fetch(
        `${API}/api/admin/search/alias-suggestions?status=pending&limit=200`,
        { headers: authHeader, credentials: 'include' },
      );
      if (!r.ok) {
        const body = await r.text().catch(() => '');
        throw new Error(`HTTP ${r.status}${body ? `: ${body.slice(0, 120)}` : ''}`);
      }
      const data = await r.json();
      setSuggestions(Array.isArray(data) ? data : (data?.suggestions || []));
    } catch (e) {
      setSuggestionsError(e?.message || 'Failed to load');
      setSuggestions([]);
    } finally {
      setSuggestionsLoading(false);
    }
  }, [authHeader]);

  const loadOverrides = useCallback(async () => {
    setOverridesLoading(true);
    setOverridesError(null);
    try {
      const r = await fetch(
        `${API}/api/admin/search/alias-overrides?limit=200`,
        { headers: authHeader, credentials: 'include' },
      );
      if (!r.ok) {
        const body = await r.text().catch(() => '');
        throw new Error(`HTTP ${r.status}${body ? `: ${body.slice(0, 120)}` : ''}`);
      }
      const data = await r.json();
      // iter273 — backend returns {overrides, count, cache_size, auto_promote_enabled}
      // Store rows as a flat array AND keep meta separately so the
      // cache-size pill stays accurate without polluting the rows array.
      const rows = Array.isArray(data) ? data : (data?.overrides || []);
      setOverrides(rows);
      setOverridesMeta({
        cache_size: typeof data?.cache_size === 'number' ? data.cache_size : 0,
        auto_promote_enabled: Boolean(data?.auto_promote_enabled),
      });
    } catch (e) {
      setOverridesError(e?.message || 'Failed to load');
      setOverrides([]);
      setOverridesMeta({ cache_size: 0, auto_promote_enabled: false });
    } finally {
      setOverridesLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    if (!token) return;
    loadEmail();
    loadZeroResults();
    loadSuggestions();
    loadOverrides();
  }, [token, loadEmail, loadZeroResults, loadSuggestions, loadOverrides]);

  // ── actions ─────────────────────────────────────────────────────────
  const askConfirm = (cfg) => setConfirm({ open: true, danger: false, ...cfg });
  const closeConfirm = () => setConfirm((c) => ({ ...c, open: false }));

  const triggerDomainVerify = (domainId) => {
    askConfirm({
      title: isArabic ? 'تحقق من النطاق' : 'Verify domain',
      description: isArabic
        ? 'سيطلب Resend إعادة فحص سجلات DNS الآن. تأكد من أنك قمت بإضافة سجلات DKIM/SPF/MX قبل المتابعة.'
        : 'Resend will be asked to re-check the DNS records now. Make sure you have added the DKIM/SPF/MX records before continuing.',
      onConfirm: async () => {
        closeConfirm();
        try {
          const r = await fetch(`${API}/api/admin/email/domains/${domainId}/verify`, {
            method: 'POST', headers: authHeader,
          });
          const j = await r.json();
          if (!r.ok) throw new Error(j.detail || `${r.status}`);
          toast.success(isArabic ? `حالة النطاق: ${j.status || 'تم الإرسال'}` : `Domain status: ${j.status || 'requested'}`);
          loadEmail();
        } catch (e) {
          toast.error(isArabic ? 'فشل التحقق' : `Verify failed: ${e.message}`);
        }
      },
    });
  };

  const approveSuggestion = (nq, targetCat) => {
    askConfirm({
      title: isArabic ? 'الموافقة على الاسم البديل' : 'Approve alias',
      description: isArabic
        ? `سيتم تعيين "${nq}" إلى الفئة "${targetCat}" — وسيستخدم البحث هذا التعيين فورًا.`
        : `"${nq}" will map to category "${targetCat}". Search will use this mapping immediately.`,
      onConfirm: async () => {
        closeConfirm();
        try {
          const url = `${API}/api/admin/search/alias-suggestions/${encodeURIComponent(nq)}/decision?decision=accepted&target_category_id=${encodeURIComponent(targetCat)}`;
          const r = await fetch(url, { method: 'POST', headers: authHeader });
          const j = await r.json();
          if (!r.ok) throw new Error(j.detail || `${r.status}`);
          toast.success(isArabic ? 'تمت الموافقة' : 'Approved');
          loadSuggestions();
          loadOverrides();
        } catch (e) {
          toast.error(isArabic ? 'فشلت الموافقة' : `Approve failed: ${e.message}`);
        }
      },
    });
  };

  const rejectSuggestion = (nq) => {
    askConfirm({
      title: isArabic ? 'رفض الاقتراح' : 'Reject suggestion',
      description: isArabic
        ? 'لن يظهر هذا الاقتراح مرة أخرى وسيُلغى أي تجاوز نشط.'
        : 'This suggestion will be hidden and any active override removed.',
      danger: true,
      onConfirm: async () => {
        closeConfirm();
        try {
          const url = `${API}/api/admin/search/alias-suggestions/${encodeURIComponent(nq)}/decision?decision=rejected`;
          const r = await fetch(url, { method: 'POST', headers: authHeader });
          const j = await r.json();
          if (!r.ok) throw new Error(j.detail || `${r.status}`);
          toast.success(isArabic ? 'تم الرفض' : 'Rejected');
          loadSuggestions();
          loadOverrides();
        } catch (e) {
          toast.error(isArabic ? 'فشل الرفض' : `Reject failed: ${e.message}`);
        }
      },
    });
  };

  // Revoke an active override = re-use the rejected decision.
  const revokeOverride = (nq) => {
    askConfirm({
      title: isArabic ? 'إلغاء التجاوز' : 'Revoke override',
      description: isArabic
        ? `سيتوقف البحث عن التعرف على "${nq}" كاسم بديل لفئة معينة.`
        : `Search will stop recognising "${nq}" as a category alias.`,
      danger: true,
      onConfirm: async () => {
        closeConfirm();
        try {
          const url = `${API}/api/admin/search/alias-suggestions/${encodeURIComponent(nq)}/decision?decision=rejected`;
          const r = await fetch(url, { method: 'POST', headers: authHeader });
          const j = await r.json();
          if (!r.ok) throw new Error(j.detail || `${r.status}`);
          toast.success(isArabic ? 'تم الإلغاء' : 'Revoked');
          loadOverrides();
          loadSuggestions();
        } catch (e) {
          toast.error(isArabic ? 'فشل الإلغاء' : `Revoke failed: ${e.message}`);
        }
      },
    });
  };

  // ── filtering ───────────────────────────────────────────────────────
  // iter273 — state is normalised to a plain array by the loaders, so
  // read it directly. Previously we re-read `.queries` / `.suggestions`
  // / `.overrides` which were undefined → permanent empty table.
  const filteredZero = useMemo(() => {
    const rows = Array.isArray(zeroResults) ? zeroResults : (zeroResults?.queries || []);
    const q = zeroResultsFilter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.normalized_query || '').toLowerCase().includes(q) ||
      (r.last_raw || '').toLowerCase().includes(q),
    );
  }, [zeroResults, zeroResultsFilter]);

  const filteredSuggestions = useMemo(() => {
    const rows = Array.isArray(suggestions) ? suggestions : (suggestions?.suggestions || []);
    const q = suggestionsFilter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.normalized_query || '').toLowerCase().includes(q) ||
      (r.suggested_category || '').toLowerCase().includes(q) ||
      (r.lang || '').toLowerCase().includes(q),
    );
  }, [suggestions, suggestionsFilter]);

  const filteredOverrides = useMemo(() => {
    const rows = Array.isArray(overrides) ? overrides : (overrides?.overrides || []);
    const q = overridesFilter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.alias_normalized || '').toLowerCase().includes(q) ||
      (r.category_id || '').toLowerCase().includes(q),
    );
  }, [overrides, overridesFilter]);

  // ── access gate ─────────────────────────────────────────────────────
  if (!user?.is_admin) {
    return (
      <div className="admin-page" dir={dir}>
        <div className="admin-access-denied">
          <AlertTriangle size={64} />
          <h2>{isArabic ? 'وصول مرفوض' : 'Access denied'}</h2>
          <p>{isArabic ? 'هذه الصفحة للمشرفين فقط.' : 'This page is for admins only.'}</p>
          <button className="back-btn" onClick={() => navigate('/')} data-testid="ash-back-home">
            {isArabic ? 'العودة للرئيسية' : 'Back to home'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page" dir={dir} data-testid="admin-system-health-page">
      {/* Header — reuses AdminModeration's admin-header */}
      <div className="admin-header">
        <BackButton
          className="admin-back-btn"
          testId="ash-back-btn"
          size={18}
          to="/admin/overview"
        />
        <div className="admin-header-left">
          <Activity size={20} />
          <h1>{isArabic ? 'صحة النظام' : 'System Health'}</h1>
        </div>
        <button
          className="admin-back-btn"
          onClick={() => { loadEmail(); loadZeroResults(); loadSuggestions(); loadOverrides(); }}
          data-testid="ash-refresh-all"
          aria-label="refresh"
        >
          <RefreshCw size={16} />
        </button>
      </div>

      {/* ───────────────── 1. Email Health ───────────────── */}
      <section className="ash-section" data-testid="ash-email-section">
        <header className="ash-section-head">
          <Mail size={18} />
          <h2>{isArabic ? 'حالة البريد الإلكتروني' : 'Email Health'}</h2>
        </header>
        {emailLoading ? (
          <div className="ash-loading"><RefreshCw size={16} className="ash-spin" /> {isArabic ? 'جاري التحميل…' : 'Loading…'}</div>
        ) : emailError ? (
          <>
            <div className="ash-error-banner" data-testid="ash-email-error-banner">
              <AlertTriangle size={14} />
              <span>{isArabic ? 'تعذر تحميل حالة البريد' : 'Could not load email status'}</span>
              <code>{emailError}</code>
            </div>
            <div className="ash-empty" data-testid="ash-email-empty">
              <Inbox size={24} />
              <p>{isArabic ? 'لا توجد بيانات حاليًا' : 'No data available'}</p>
            </div>
          </>
        ) : !emailStatus ? (
          <div className="ash-empty">{isArabic ? 'لا توجد بيانات' : 'No data'}</div>
        ) : (
          <>
            <div className="ash-kv-grid">
              <div><span>Resend</span><b>{emailStatus.resend_available ? 'OK' : '—'}</b></div>
              <div><span>Sender</span><b>{emailStatus.sender_name} &lt;{emailStatus.sender}&gt;</b></div>
              <div><span>Reply-To</span><b>{emailStatus.reply_to}</b></div>
            </div>

            {(emailStatus.domains || []).map((d) => (
              <div key={d.id} className="ash-domain-card" data-testid={`ash-domain-${d.id}`}>
                <div className="ash-domain-head">
                  <ShieldCheck size={14} />
                  <strong>{d.name}</strong>
                  <span className={`ash-pill ash-pill-${d.status === 'verified' ? 'ok' : 'warn'}`}>{d.status}</span>
                  <button
                    className="ash-mini-btn"
                    onClick={() => triggerDomainVerify(d.id)}
                    data-testid={`ash-verify-${d.id}`}
                  >
                    {isArabic ? 'تحقق الآن' : 'Verify now'}
                  </button>
                </div>
                {Array.isArray(d.dns_live_check) && d.dns_live_check.length > 0 && (
                  <ul className="ash-dns-rows">
                    {d.dns_live_check.map((rec, i) => (
                      <li key={i} className={rec.found ? 'found' : 'missing'}>
                        {rec.found ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                        <code>{rec.type}</code> <code>{rec.host}</code>
                        <span className="ash-dns-status">{rec.found ? (isArabic ? 'مرئي' : 'visible') : (rec.err || (isArabic ? 'مفقود' : 'missing'))}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}

            <details className="ash-failures">
              <summary>
                {isArabic ? `آخر إخفاقات الإرسال (${emailStatus.recent_failures?.length || 0})` : `Recent send failures (${emailStatus.recent_failures?.length || 0})`}
              </summary>
              {(emailStatus.recent_failures || []).length === 0 ? (
                <div className="ash-empty-small">{isArabic ? 'لا توجد إخفاقات حديثة' : 'No recent failures'}</div>
              ) : (
                <ul className="ash-fail-list">
                  {emailStatus.recent_failures.map((f, i) => (
                    <li key={i}>
                      <code>{f.notification_type}</code>
                      <span>{f.email}</span>
                      <em>{f.error}</em>
                      <time>{f.created_at}</time>
                    </li>
                  ))}
                </ul>
              )}
            </details>
          </>
        )}
      </section>

      {/* ───────────────── 2. Failed Searches ───────────────── */}
      <section className="ash-section" data-testid="ash-zero-results-section">
        <header className="ash-section-head">
          <Search size={18} />
          <h2>{isArabic ? 'عمليات البحث بدون نتائج' : 'Failed Searches'}</h2>
          <div className="ash-section-actions">
            <select
              value={zeroResultsDays}
              onChange={(e) => setZeroResultsDays(parseInt(e.target.value, 10))}
              className="ash-range-select"
              data-testid="ash-zero-range"
              aria-label="range"
            >
              <option value={7}>{isArabic ? '7 أيام' : '7 days'}</option>
              <option value={14}>{isArabic ? '14 يوم' : '14 days'}</option>
              <option value={30}>{isArabic ? '30 يوم' : '30 days'}</option>
            </select>
          </div>
        </header>

        {(filteredZero.length || 0) > 20 && (
          <div className="ash-table-filter">
            <Filter size={14} />
            <Input
              type="text"
              value={zeroResultsFilter}
              onChange={(e) => setZeroResultsFilter(e.target.value)}
              placeholder={isArabic ? 'تصفية…' : 'Filter…'}
              data-testid="ash-zero-filter-input"
            />
          </div>
        )}

        {zeroResultsError && (
          <div className="ash-error-banner" data-testid="ash-zero-error-banner">
            <AlertTriangle size={14} />
            <span>{isArabic ? 'تعذر تحميل البيانات' : 'Could not load data'}</span>
            <code>{zeroResultsError}</code>
          </div>
        )}

        {zeroResultsLoading ? (
          <div className="ash-loading"><RefreshCw size={16} className="ash-spin" /> {isArabic ? 'جاري التحميل…' : 'Loading…'}</div>
        ) : filteredZero.length === 0 ? (
          <div className="ash-empty" data-testid="ash-zero-empty">
            <Inbox size={28} />
            <p>{isArabic ? 'لا توجد عمليات بحث فاشلة في هذه الفترة.' : 'No failed searches in this range.'}</p>
          </div>
        ) : (
          <div className="ash-table-wrap">
            <table className="ash-table" data-testid="ash-zero-table">
              <thead>
                <tr>
                  <th>{isArabic ? 'الاستعلام' : 'Query'}</th>
                  <th>{isArabic ? 'التكرار' : 'Count'}</th>
                  <th>{isArabic ? 'اللغة' : 'Lang'}</th>
                  <th>{isArabic ? 'آخر مرة' : 'Last seen'}</th>
                </tr>
              </thead>
              <tbody>
                {filteredZero.map((r, i) => (
                  <tr key={`${r.normalized_query}-${i}`} data-testid={`ash-zero-row-${i}`}>
                    <td><code>{r.normalized_query}</code><div className="ash-raw">{r.last_raw}</div></td>
                    <td><b>{r.count}</b></td>
                    <td>{r.lang || '—'}</td>
                    <td className="ash-time">{(r.last_seen || '').slice(0, 16).replace('T', ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ───────────────── 3. Alias Suggestions ───────────────── */}
      <section className="ash-section" data-testid="ash-suggestions-section">
        <header className="ash-section-head">
          <TrendingUp size={18} />
          <h2>{isArabic ? 'اقتراحات الأسماء البديلة' : 'Alias Suggestions'}</h2>
        </header>

        {(filteredSuggestions.length || 0) > 20 && (
          <div className="ash-table-filter">
            <Filter size={14} />
            <Input
              type="text"
              value={suggestionsFilter}
              onChange={(e) => setSuggestionsFilter(e.target.value)}
              placeholder={isArabic ? 'تصفية…' : 'Filter…'}
              data-testid="ash-suggestions-filter-input"
            />
          </div>
        )}

        {suggestionsError && (
          <div className="ash-error-banner" data-testid="ash-suggestions-error-banner">
            <AlertTriangle size={14} />
            <span>{isArabic ? 'تعذر تحميل البيانات' : 'Could not load data'}</span>
            <code>{suggestionsError}</code>
          </div>
        )}

        {suggestionsLoading ? (
          <div className="ash-loading"><RefreshCw size={16} className="ash-spin" /> {isArabic ? 'جاري التحميل…' : 'Loading…'}</div>
        ) : filteredSuggestions.length === 0 ? (
          <div className="ash-empty" data-testid="ash-suggestions-empty">
            <Inbox size={28} />
            <p>{isArabic ? 'لا توجد اقتراحات قيد المراجعة.' : 'No pending suggestions to review.'}</p>
          </div>
        ) : (
          <div className="ash-table-wrap">
            <table className="ash-table" data-testid="ash-suggestions-table">
              <thead>
                <tr>
                  <th>{isArabic ? 'الاستعلام' : 'Query'}</th>
                  <th>{isArabic ? 'التكرار' : 'Count'}</th>
                  <th>{isArabic ? 'الثقة' : 'Conf'}</th>
                  <th>{isArabic ? 'الفئة المقترحة' : 'Suggested'}</th>
                  <th>{isArabic ? 'إجراء' : 'Action'}</th>
                </tr>
              </thead>
              <tbody>
                {filteredSuggestions.map((s, i) => {
                  const cat = s.suggested_category;
                  return (
                    <tr key={`${s.normalized_query}-${i}`} data-testid={`ash-sug-row-${i}`}>
                      <td><code>{s.normalized_query}</code></td>
                      <td><b>{s.count}</b></td>
                      <td>{typeof s.confidence_ratio === 'number' ? s.confidence_ratio.toFixed(2) : '—'}</td>
                      <td>{cat ? <span className="ash-pill ash-pill-info">{cat}</span> : <em>—</em>}</td>
                      <td className="ash-row-actions">
                        <button
                          className="ash-mini-btn ash-mini-ok"
                          disabled={!cat}
                          onClick={() => approveSuggestion(s.normalized_query, cat)}
                          data-testid={`ash-approve-${i}`}
                          title={!cat ? (isArabic ? 'لا توجد فئة مقترحة' : 'No suggested category') : ''}
                        >
                          {isArabic ? 'موافقة' : 'Approve'}
                        </button>
                        <button
                          className="ash-mini-btn ash-mini-danger"
                          onClick={() => rejectSuggestion(s.normalized_query)}
                          data-testid={`ash-reject-${i}`}
                        >
                          {isArabic ? 'رفض' : 'Reject'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ───────────────── 4. Active Alias Overrides ───────────────── */}
      <section className="ash-section" data-testid="ash-overrides-section">
        <header className="ash-section-head">
          <CheckCircle2 size={18} />
          <h2>{isArabic ? 'التجاوزات النشطة' : 'Active Overrides'}</h2>
          <span className="ash-meta-pill" data-testid="ash-overrides-meta">
            cache: {overridesMeta.cache_size ?? 0} · auto: {overridesMeta.auto_promote_enabled ? 'on' : 'off'}
          </span>
        </header>

        {(filteredOverrides.length || 0) > 20 && (
          <div className="ash-table-filter">
            <Filter size={14} />
            <Input
              type="text"
              value={overridesFilter}
              onChange={(e) => setOverridesFilter(e.target.value)}
              placeholder={isArabic ? 'تصفية…' : 'Filter…'}
              data-testid="ash-overrides-filter-input"
            />
          </div>
        )}

        {overridesError && (
          <div className="ash-error-banner" data-testid="ash-overrides-error-banner">
            <AlertTriangle size={14} />
            <span>{isArabic ? 'تعذر تحميل البيانات' : 'Could not load data'}</span>
            <code>{overridesError}</code>
          </div>
        )}

        {overridesLoading ? (
          <div className="ash-loading"><RefreshCw size={16} className="ash-spin" /> {isArabic ? 'جاري التحميل…' : 'Loading…'}</div>
        ) : filteredOverrides.length === 0 ? (
          <div className="ash-empty" data-testid="ash-overrides-empty">
            <Inbox size={28} />
            <p>{isArabic ? 'لا توجد تجاوزات نشطة.' : 'No active overrides yet.'}</p>
          </div>
        ) : (
          <div className="ash-table-wrap">
            <table className="ash-table" data-testid="ash-overrides-table">
              <thead>
                <tr>
                  <th>{isArabic ? 'الاسم البديل' : 'Alias'}</th>
                  <th>{isArabic ? 'الفئة' : 'Category'}</th>
                  <th>{isArabic ? 'المصدر' : 'Source'}</th>
                  <th>{isArabic ? 'الموافق' : 'Approved by'}</th>
                  <th>{isArabic ? 'إجراء' : 'Action'}</th>
                </tr>
              </thead>
              <tbody>
                {filteredOverrides.map((o, i) => (
                  <tr key={`${o.alias_normalized}-${i}`} data-testid={`ash-ov-row-${i}`}>
                    <td><code>{o.alias_normalized}</code></td>
                    <td><span className="ash-pill ash-pill-info">{o.category_id}</span></td>
                    <td>{o.source}</td>
                    <td className="ash-time">{o.approved_by}</td>
                    <td className="ash-row-actions">
                      <button
                        className="ash-mini-btn ash-mini-danger"
                        onClick={() => revokeOverride(o.alias_normalized)}
                        data-testid={`ash-revoke-${i}`}
                      >
                        {isArabic ? 'إلغاء' : 'Revoke'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Shared confirmation modal */}
      <AlertDialog open={confirm.open} onOpenChange={(o) => !o && closeConfirm()}>
        <AlertDialogContent dir={dir} data-testid="ash-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ash-confirm-cancel">
              {isArabic ? 'إلغاء' : 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirm.onConfirm && confirm.onConfirm()}
              className={confirm.danger ? 'ash-confirm-danger' : ''}
              data-testid="ash-confirm-ok"
            >
              {isArabic ? 'متابعة' : 'Continue'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
